import React, {useCallback, useMemo, useRef, useEffect, useState} from "react";
import {
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    message,
    Progress,
    Row,
    Select,
    Space,
    Steps,
    Tag,
    Timeline,
    Tooltip,
    Typography,
    Upload,
    Segmented,
    Radio,
    Descriptions
} from "antd";
import {
    ArrowLeftOutlined,
    ArrowRightOutlined,
    BankOutlined,
    CarOutlined,
    CheckCircleOutlined,
    CloudUploadOutlined,
    DollarOutlined,
    FileDoneOutlined,
    FileSearchOutlined,
    FileTextOutlined,
    HomeOutlined,
    SafetyCertificateOutlined,
    SendOutlined,
    ToolOutlined,
    WalletOutlined,
    LineChartOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosinstance from "util/Api";
import {CisUI} from "../../../../util/CISUI";
import "./AssetAcquisitionModern.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

const assetTypes = [
    {label: "Building", value: "Church Building", icon: <HomeOutlined/>},
    {label: "Mission House", value: "Mission House", icon: <HomeOutlined/>},
    {label: "Vehicle", value: "Vehicle (Church Van)", icon: <CarOutlined/>},
    {label: "Furniture", value: "Furniture", icon: <BankOutlined/>},
    {label: "Equipment", value: "Equipment", icon: <ToolOutlined/>},
    {label: "Other", value: "Other", icon: <ToolOutlined/>},
];

const priorityLevels = ["Critical", "Urgent", "High", "Medium", "Low"];
const acquisitionMethods = ["Purchase", "Donation", "Transfer", "Lease"];
const assetConditions = ["Excellent", "Good", "Fair", "Ok", "Need Repair/Upgrade"];

const initialFormValues = {
    asset_type: "Church Building",
    request_date: dayjs(),
    priority_level: "Medium",
    quantity: 1,
};


export const formatNumber = (
    value,
    userTyping = false,
    decimals = null
) => {
    if (value === undefined || value === null || value === "") {
        return "";
    }

    // Don't format while typing
    if (userTyping) {
        return String(value);
    }

    const options = {};

    if (decimals !== null) {
        options.maximumFractionDigits = decimals;
        options.minimumFractionDigits = decimals;
    }

    return Number(value).toLocaleString("en-US", options);
};

export const parseNumber = (value) => {
    if (!value) return "";
    return value.replace(/,/g, "");
};

const selectOptions = (items) => items.map((x) => ({label: x, value: x}));

const money = (value) =>
    Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const compactAmount = (value) => {
    const amount = Number(value || 0);
    const absolute = Math.abs(amount);

    const formatCompact = (divisor, suffix) => {
        const compactValue = amount / divisor;
        const decimals = Math.abs(compactValue) >= 100 ? 0 : Math.abs(compactValue) >= 10 ? 1 : 2;

        return `${Number(compactValue.toFixed(decimals))}${suffix}`;
    };

    if (absolute >= 1_000_000_000_000) return formatCompact(1_000_000_000_000, "T");
    if (absolute >= 1_000_000_000) return formatCompact(1_000_000_000, "B");
    if (absolute >= 1_000_000) return formatCompact(1_000_000, "M");
    if (absolute >= 1_000) return formatCompact(1_000, "K");

    return Number(amount.toFixed(2)).toLocaleString("en-US");
};

export default function AssetAcquisitionWorkspace() {
    const [form] = Form.useForm();

    const [current, setCurrent] = useState(0);
    const [assetType, setAssetType] = useState("Church Building");
    const [nationHelp, setNationHelp] = useState("No");
    const [incomePotential, setIncomePotential] = useState("No");
    const [loading, setLoading] = useState(false);
    const [reviewValues, setReviewValues] = useState(initialFormValues);

    const [cashflowdata, setCashflowData] = useState({
        'adult_membership' : 0,
        'tithe_paying_members' : 0,
        'average_monthly_tithes' : 0,
        'average_monthly_expense' : 0,
        'amount_in_coffers_ldf' : 0,
        'average_monthly_remittance' : 0,
        'monthly_data' : [],
        'emergency_fund_available' : 0,
        'building_fund_available' : 0
    });

    const isBuilding = useMemo(
        () => ["Church Building", "Mission House"].includes(assetType),
        [assetType]
    );

    const isVehicle = useMemo(
        () => String(assetType || "").includes("Vehicle"),
        [assetType]
    );

    const isFurniture = assetType === "Furniture";
    const isEquipment = assetType === "Equipment";

    const estimatedCost = Number(reviewValues?.estimated_cost || 0);

    const availableFunds = Number(
        reviewValues?.financial_readiness?.amount_available ||
        reviewValues?.financial_readiness?.available_funds ||
        0
    );

    const monthlyIncome =
        Number(reviewValues?.cash_flow_analysis?.average_monthly_tithes || 0);

    const monthlyExpense =
        Number(cashflowdata?.average_monthly_expense || 0);

    const anticipatedAmount =
        Number(
            reviewValues?.financial_readiness?.anticipated_amount || 0
        );

    const liquidityRatio =
        monthlyExpense > 0
            ? (availableFunds / monthlyExpense).toFixed(2)
            : "0.00";

    const debtIncomeRatio =
        monthlyIncome > 0
            ? (
                (anticipatedAmount / (monthlyIncome * 12)) *
                100
            ).toFixed(1)
            : "0.0";

    const operatingReserveMonths =
        monthlyExpense > 0
            ? (availableFunds / monthlyExpense).toFixed(1)
            : "0";

    const netAssetPosition =
        availableFunds - anticipatedAmount;



    useEffect(() => {
        const loadData = async () => {
            try {
                const res = await axiosinstance.get("cashflowanalysis");

                if (res.data.status === 1) {
                    setCashflowData(res.data.data);
                }
            } catch (err) {
                console.error(err);
            }
        };

        loadData();
    }, []);


    const monthlyData = cashflowdata?.monthly_data || [];

    let givingTrend = "No Data";

    if (monthlyData.length >= 2) {
        const first = Number(monthlyData[0]?.amount || 0);
        const last = Number(monthlyData[monthlyData.length - 1]?.amount || 0);

        if (last > first * 1.05) {
            givingTrend = "Growing";
        } else if (last < first * 0.95) {
            givingTrend = "Declining";
        } else {
            givingTrend = "Stable";
        }
    }


    const fundingGap = Math.max(estimatedCost - availableFunds, 0);

    const fundingPercent =
        estimatedCost > 0
            ? Math.min(Math.round((availableFunds / estimatedCost) * 100), 100)
            : 0;

    const documentScore = Array.isArray(reviewValues?.checklist)
        ? Math.min(Math.round((reviewValues.checklist.length / 12) * 100), 100)
        : 0;

    const riskAnswers = reviewValues?.risk_assessment || {};

    const selectedRiskCount = Object.entries(riskAnswers).filter(
        ([key, value]) => !key.includes("_explanation") && value === "Yes"
    ).length;

    const riskScore = Math.max(100 - selectedRiskCount * 12, 20);

    const readinessScore = Math.round(
        (fundingPercent + documentScore + riskScore) / 3
    );

    const completion = useMemo(
        () => Math.round(((current + 1) / 7) * 100),
        [current]
    );


    const fundingSource =
        reviewValues?.financial_readiness?.funding_source || "";

    const budgetAllocationStatus =
        availableFunds >= estimatedCost
            ? "Available"
            : "Insufficient";

    const fundingSourceStatus =
        fundingSource
            ? "Confirmed"
            : "Pending";

    const affordabilityStatus =
        fundingGap <= 0
            ? "Affordable"
            : "Not Affordable";

    const budgetAllocationRemarks =
        availableFunds >= estimatedCost
            ? "Sufficient budget is available to cover the acquisition."
            : `Additional ${money(fundingGap)} funding is required.`;

    const fundingSourceRemarks =
        fundingSource
            ? `${fundingSource} has been identified as the primary funding source.`
            : "No funding source has been specified.";

    const affordabilityRemarks =
        liquidityRatio >= 2 && operatingReserveMonths >= 3
            ? "Financial position indicates that the acquisition is affordable."
            : "Financial capacity should be reviewed before approval.";

    const financeRecommendation =
        availableFunds >= estimatedCost &&
        liquidityRatio >= 2 &&
        operatingReserveMonths >= 3
            ? "APPROVED"
            : "REVIEW REQUIRED";



    const approvalLevel =
        estimatedCost >= 100000
            ? "National"
            : estimatedCost >= 50000
                ? "Area"
                : estimatedCost >= 10000
                    ? "District"
                    : "Local";

    const resetToInitial = useCallback(() => {
        form.resetFields();
        form.setFieldsValue(initialFormValues);
        setReviewValues(initialFormValues);
        setAssetType("Church Building");
        setCurrent(0);
        setNationHelp("No");
        setIncomePotential("No");
    }, [form]);

    const handleAssetTypeChange = useCallback(
        (value) => {
            const quantity = ["Church Building", "Mission House"].includes(value)
                ? undefined
                : 1;

            setAssetType(value);

            form.setFieldsValue({
                asset_type: value,
                asset_specifications: {},
                quantity,
            });

            setReviewValues((prev) => ({
                ...prev,
                asset_type: value,
                asset_specifications: {},
                quantity,
            }));
        },
        [form]
    );



    const next = useCallback(async () => {
        try {
            await form.validateFields();

            const allValues = form.getFieldsValue(true);
            setReviewValues(allValues);

            setCurrent((prev) => Math.min(prev + 1, 6));
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch {
            message.warning("Please complete required fields.");
        }
    }, [form]);

    const prev = useCallback(() => {
        setCurrent((prev) => Math.max(prev - 1, 0));
        window.scrollTo({top: 0, behavior: "smooth"});
    }, []);

    const submit = useCallback(
        async (status = "Submitted") => {
            try {
                const data = await form.validateFields();

                const payload = {
                    ...data,
                    status,
                    require_assistance_from_nation: nationHelp === "Yes",
                    income_potential_enabled: incomePotential === "Yes",
                    request_date: data.request_date
                        ? dayjs(data.request_date).format("YYYY-MM-DD")
                        : null,
                    required_by_date: data.required_by_date
                        ? dayjs(data.required_by_date).format("YYYY-MM-DD")
                        : null,
                    asset_specifications: {
                        ...(data.asset_specifications || {}),
                        construction_date: data.asset_specifications?.construction_date
                            ? dayjs(data.asset_specifications.construction_date).format(
                                "YYYY-MM-DD"
                            )
                            : null,
                    },
                };

                setLoading(true);

                const res = await axiosinstance.post("/asset-acquisitions", payload);

                if (res.data.status === 1) {
                    message.success(
                        status === "Draft"
                            ? "Request saved as draft."
                            : "Request submitted successfully."
                    );
                    resetToInitial();
                } else {
                    message.error(res.data.msg || "Failed to save request.");
                }
            } catch (error) {
                if (error?.errorFields) {
                    message.warning("Please complete required fields.");
                    return;
                }

                message.error(error?.response?.data?.msg || "Something went wrong.");
            } finally {
                setLoading(false);
            }
        },
        [form, nationHelp, incomePotential, resetToInitial]
    );

    const steps = useMemo(
        () => [
            {title: "Request", icon: <FileTextOutlined/>},
            {
                title: "Asset Details",
                icon: isVehicle ? (
                    <CarOutlined/>
                ) : isBuilding ? (
                    <HomeOutlined/>
                ) : (
                    <ToolOutlined/>
                ),
            },
            {title: "Funding", icon: <WalletOutlined/>},
            {title: "Cash Flow", icon: <DollarOutlined/>},
            {title: "Risk & Cost", icon: <SafetyCertificateOutlined/>},
            {title: "Documents", icon: <CloudUploadOutlined/>},
            {title: "Review", icon: <FileSearchOutlined/>},
        ],
        [isVehicle, isBuilding]
    );

    const SectionHeader = ({icon, title, desc}) => (
        <Space align="start" className="aa-section-header">
            <div className="aa-icon-box">{icon}</div>
            <div>
                <Title level={4} className="aa-section-title">
                    {title}
                </Title>
                <Text type="secondary">{desc}</Text>
            </div>
        </Space>
    );

    const AmountDisplay = ({value, className = ""}) => (
        <Tooltip
            title={money(value)}
            placement="top"
            mouseEnterDelay={0.15}
            overlayClassName="aa-amount-tooltip"
        >
            <span
                className={`aa-compact-amount ${className}`}
                aria-label={money(value)}
            >
                {compactAmount(value)}
            </span>
        </Tooltip>
    );

    const MetricCard = ({title, value, rawValue, icon, status}) => (
        <Card bordered={false} className={`aa-metric-card ${status || ""}`}>
            <Space align="center" size={14}>
                <div className="aa-metric-icon">{icon}</div>
                <div className="aa-metric-content">
                    <Text type="secondary">{title}</Text>
                    <Title level={4}>
                        {rawValue !== undefined ? (
                            <AmountDisplay value={rawValue}/>
                        ) : (
                            value
                        )}
                    </Title>
                </div>
            </Space>
        </Card>
    );

    const BasicRequest = () => (
        <>
            <SectionHeader
                icon={<FileTextOutlined/>}
                title="Request Information"
                desc="Start with asset category, priority, acquisition method, cost, and justification."
            />

            <div className="aa-section">

                <Form.Item name="asset_type" hidden>
                    <Input/>
                </Form.Item>

                <Form.Item label="Asset Type" name="asset_type" required>
                    <Row gutter={[14, 14]}>
                        <Segmented
                            options={assetTypes}
                            block
                            value={assetType}
                            onChange={(value) => {
                                const quantity = ["Church Building", "Mission House"].includes(value)
                                    ? undefined
                                    : 1;

                                setAssetType(value);

                                form.setFieldsValue({
                                    asset_type: value,
                                    asset_specifications: {},
                                    quantity,
                                });

                                setReviewValues((prev) => ({
                                    ...prev,
                                    asset_type: value,
                                    asset_specifications: {},
                                    quantity,
                                }));
                            }}
                        />
                    </Row>
                </Form.Item>

                <Divider/>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Request Date"
                            name="request_date"
                            rules={[{required: true, message: "Request date is required"}]}
                        >
                            <DatePicker  format={CisUI().dateFormat} style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Required By Date"
                            name="required_by_date"
                            rules={[
                                {required: true, message: "Required by date is required"},
                                {
                                    validator(_, value) {
                                        if (!value || !value.isBefore(dayjs(), "day")) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error("Date cannot be backdated"));
                                    },
                                },
                            ]}
                        >
                            <DatePicker style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Priority Level"
                            name="priority_level"
                            rules={[{required: true, message: "Priority is required"}]}
                        >
                            <Segmented options={selectOptions(priorityLevels)} block/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Acquisition Method"
                            name="acquisition_method"
                            rules={[
                                {required: true, message: "Acquisition method is required"},
                            ]}
                        >
                            <Segmented options={selectOptions(acquisitionMethods)} block/>
                        </Form.Item>
                    </Col>

                    {!isBuilding && (
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Quantity"
                                name="quantity"
                                rules={[{required: true, message: "Quantity is required"}]}
                            >
                                <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={1} style={{width: "100%"}}/>
                            </Form.Item>
                        </Col>
                    )}

                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Estimated Cost"
                            name="estimated_cost"
                            rules={[
                                {required: true, message: "Estimated cost is required"},
                            ]}
                        >
                            <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24}>
                        <Form.Item
                            label="Justification / Purpose"
                            name="justification"
                            rules={[
                                {required: true, message: "Justification is required"},
                            ]}
                        >
                            <TextArea
                                rows={4}
                                placeholder="Explain why this acquisition is needed"
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24}>
                        <Form.Item
                            label="Asset Description/Note"
                            name="asset_description"
                            rules={[
                                {required: false, message: "Asset description is required"},
                            ]}
                        >
                            <TextArea rows={4} placeholder="Describe the asset clearly"/>
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </>
    );

    const BuildingFields = () => (
        <Card className="aa-section" title={"Building / Property Specification"}>
            <Row gutter={[16, 16]}>
                {[
                    ["location", "Location", "input", true],
                    ["plot_number", "Plot Number", "input", true],
                    ["title_deed_number", "Title Deed Number", "input", false],
                    ["land_size", "Land Size", "input", true],
                    ["building_size", "Building Size", "input", true],
                    ["current_use", "Current Use", "input", false],
                    ["occupancy_capacity", "Occupancy Capacity", "number", true],
                    ["parking_spaces", "Parking Spaces", "number", false],
                    ["property_value", "Property Value", "number", true],
                ].map(([name, label, type, required]) => (
                    <Col xs={24} md={8} key={name}>
                        <Form.Item
                            label={label}
                            name={["asset_specifications", name]}
                            rules={
                                required
                                    ? [{required: true, message: `${label} is required`}]
                                    : []
                            }
                        >
                            {type === "number" ? (
                                <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                            ) : (
                                <Input placeholder={label}/>
                            )}
                        </Form.Item>
                    </Col>
                ))}

                <Col xs={24} md={8}>
                    <Form.Item
                        label="Construction Date"
                        name={["asset_specifications", "construction_date"]}
                    >
                        <DatePicker style={{width: "100%"}}/>
                    </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                    <Form.Item
                        label="Occupancy Status"
                        name={["asset_specifications", "occupancy_status"]}
                        rules={[
                            {required: true, message: "Occupancy status is required"},
                        ]}
                    >
                        <Select
                            options={selectOptions([
                                "Vacant",
                                "Occupied",
                                "Partially Occupied",
                                "Under Construction",
                                "Under Renovation",
                            ])}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                    <Form.Item
                        label="Asset Condition"
                        name={["asset_specifications", "asset_condition"]}
                        rules={[
                            {required: true, message: "Asset condition is required"},
                        ]}
                    >
                        <Select options={selectOptions(assetConditions)}/>
                    </Form.Item>
                </Col>

                <Col xs={24} md={8}>
                    <Form.Item
                        label="Roof Condition"
                        name={["asset_specifications", "roof_condition"]}
                        rules={[{required: true, message: "Roof condition is required"}]}
                    >
                        <Select options={selectOptions(assetConditions)}/>
                    </Form.Item>
                </Col>
            </Row>
        </Card>
    );

    const VehicleFields = () => (
        <Card className="aa-section" title="Vehicle Specification">
            <Row gutter={[16, 16]}>
                {[
                    ["manufacturer", "Manufacturer", "input", true],
                    ["brand", "Brand", "input", true],
                    ["model_number", "Model Number", "input", true],
                    ["capacity_size", "Capacity / Size", "input", true],
                    ["color", "Color", "input", false],
                    ["year_manufactured", "Year Manufactured", "number", true],
                    ["asset_value", "Asset Value", "number", true],
                    ["mileage", "Mileage", "number", false],
                    ["current_book_value", "Current Book Value", "number", false],
                    ["vin_chassis_number", "VIN / Chassis Number", "input", false],
                ].map(([name, label, type, required]) => (
                    <Col xs={24} md={8} key={name}>
                        <Form.Item
                            label={label}
                            name={["asset_specifications", name]}
                            rules={
                                required
                                    ? [{required: true, message: `${label} is required`}]
                                    : []
                            }
                        >
                            {type === "number" ? (
                                <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                            ) : (
                                <Input
                                    key={name}
                                    type="text"
                                    placeholder={label}
                                    autoComplete="off"
                                />
                            )}
                        </Form.Item>
                    </Col>
                ))}

                <Col xs={24} md={8}>
                    <Form.Item
                        label="Vehicle Status"
                        name={["asset_specifications", "state_of_car"]}
                        rules={[{required: true, message: "Vehicle Status is required"}]}
                    >
                        <Select options={selectOptions(["Excellent/New", "Good", "Fair"])}/>
                    </Form.Item>
                </Col>
            </Row>
        </Card>
    );

    const SimpleAssetFields = ({tagColor = "default", tagText, fields}) => (
        <Card className="aa-section" title={tagText}>
            <Row gutter={[16, 16]}>
                {fields.map(([name, label, type, required, options]) => (
                    <Col xs={24} md={8} key={name}>
                        <Form.Item
                            label={label}
                            name={["asset_specifications", name]}
                            rules={
                                required
                                    ? [{required: true, message: `${label} is required`}]
                                    : []
                            }
                        >
                            {options ? (
                                <Select options={selectOptions(options)}/>
                            ) : type === "number" ? (
                                <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                            ) : (
                                <Input placeholder={label}/>
                            )}
                        </Form.Item>
                    </Col>
                ))}
            </Row>
        </Card>
    );

    const AssetDetails = () => {
        if (isBuilding) return <BuildingFields/>;
        if (isVehicle) return <VehicleFields/>;

        if (isFurniture) {
            return (
                <SimpleAssetFields
                    tagColor="purple"
                    tagText="Furniture Specification"
                    fields={[
                        [
                            "furniture_type",
                            "Furniture Type",
                            "select",
                            true,
                            ["Chair", "Table", "Desk", "Cabinet", "Bench", "Shelf", "Other"],
                        ],
                        ["material", "Material", "input", false],
                        ["brand", "Brand", "input", false],
                        ["color", "Color", "input", false],
                        ["unit_cost", "Unit Cost", "number", true],
                        ["asset_condition", "Condition", "select", true, assetConditions],
                    ]}
                />
            );
        }

        if (isEquipment) {
            return (
                <SimpleAssetFields
                    tagColor="cyan"
                    tagText="Equipment Specification"
                    fields={[
                        ["equipment_name", "Equipment Name", "input", true],
                        ["brand", "Brand", "input", false],
                        ["model", "Model", "input", false],
                        ["serial_number", "Serial Number", "input", false],
                        ["warranty_period", "Warranty Period", "input", false],
                        ["asset_value", "Asset Value", "number", true],
                        ["asset_condition", "Condition", "select", true, assetConditions],
                    ]}
                />
            );
        }

        return (
            <SimpleAssetFields
                tagText="Other Asset Specification"
                fields={[
                    ["asset_name", "Asset Name", "input", true],
                    ["brand", "Brand", "input", false],
                    ["model", "Model", "input", false],
                    ["asset_value", "Asset Value", "number", true],
                    ["asset_condition", "Condition", "select", true, assetConditions],
                ]}
            />
        );
    };

    const AssetDetailsWrapper = () => (
        <>
            <SectionHeader
                icon={
                    isVehicle ? <CarOutlined/> : isBuilding ? <HomeOutlined/> : <ToolOutlined/>
                }
                title="Asset Specifications"
                desc="Complete dynamic details based on the selected asset type."
            />

            <AssetDetails/>
        </>
    );

    const Funding = () => (
        <>
            <SectionHeader
                icon={<WalletOutlined/>}
                title="Funding & Financing"
                desc="Track funding readiness, available money, gap, and financing terms."
            />

            <div className="aa-section">
                <Row gutter={[18, 18]}>
                    <Col xs={24} lg={8}>
                        <Card bordered={false} className="aa-funding-card">
                            <Progress type="dashboard" percent={fundingPercent}/>
                            <Title level={5}>Funding Readiness</Title>
                            <Text type="secondary">
                                Available funds compared with estimated asset cost.
                            </Text>
                        </Card>
                    </Col>

                    <Col xs={24} lg={16}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={8}>
                                <MetricCard
                                    title={"Estimated Cost"+" ($)"}
                                    rawValue={estimatedCost}
                                    icon={<DollarOutlined/>}
                                />
                            </Col>
                            <Col xs={24} md={8}>
                                <MetricCard
                                    title="Available Funds"
                                    rawValue={availableFunds}
                                    icon={<WalletOutlined/>}
                                    status="success"
                                />
                            </Col>
                            <Col xs={24} md={8}>
                                <MetricCard
                                    title="Funding Gap"
                                    rawValue={fundingGap}
                                    icon={<SafetyCertificateOutlined/>}
                                    status={fundingGap > 0 ? "danger" : "success"}
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Divider/>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Funding Source"
                            name={["financial_readiness", "funding_source"]}
                            rules={[
                                {required: true, message: "Funding source is required"},
                            ]}
                        >
                            <Select
                                options={selectOptions([
                                    "Church Budget",
                                    "Special Offering",
                                    "Grant",
                                    "Donation",
                                ])}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item label="Financing Options">
                            <Select
                                options={selectOptions(["Mortgage", "Loan", "Grant", "Non-Financial Support"])}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item label="Require Assistance from Nation?">
                            <Select
                                value={nationHelp}
                                onChange={setNationHelp}
                                options={selectOptions(["No", "Yes"])}
                            />
                        </Form.Item>
                    </Col>

                    {nationHelp === "Yes" ? (
                        <>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Anticipated Amount"
                                    name={["financial_readiness", "anticipated_amount"]}
                                    rules={[
                                        {
                                            required: true,
                                            message: "Anticipated amount is required",
                                        },
                                    ]}
                                >
                                    <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Available Funds"
                                    name={["financial_readiness", "available_funds"]}
                                >
                                    <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>
                        </>
                    ) : (
                        <>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Amount Available"
                                    name={["financial_readiness", "amount_available"]}
                                    rules={[
                                        {
                                            required: true,
                                            message: "Amount available is required",
                                        },
                                    ]}
                                >
                                    <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                                </Form.Item>
                            </Col>

                            <Col xs={24} md={16}>
                                <Form.Item
                                    label="Mode of Payment"
                                    name={["financial_readiness", "payment_mode"]}
                                >
                                    <Select
                                        options={selectOptions([
                                            "Cash",
                                            "Financing",
                                            "Installment",
                                            "Credit",
                                            "Other",
                                        ])}
                                    />
                                </Form.Item>
                            </Col>
                        </>
                    )}
                </Row>

                <Divider/>

                <Title level={5}>Financing Terms</Title>

                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Interest Rate"
                            name={["financing_terms", "interest_rate"]}
                        >
                            <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} addonAfter="%" style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item label="Loan Term" name={["financing_terms", "loan_term"]}>
                            <Input placeholder="Example: 5 years"/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Monthly Payment"
                            name={["financing_terms", "monthly_payment"]}
                        >
                            <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={8}>
                        <Form.Item label="Rate Type" name={["financing_terms", "rate_type"]}>
                            <Select options={selectOptions(["Fixed", "Variable"])}/>
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </>
    );

    const CashFlow = () => (
        <>
            <SectionHeader
                icon={<DollarOutlined/>}
                title="Cash Flow Analysis"
                desc="Review membership, tithe, remittance, reserves, and financial health."
            />

            <div className="aa-section">
                <Row gutter={[16, 16]}>
                    {[
                        ["adult_membership", "Adult Membership", true],
                        ["tithe_paying_members", "Tithe Paying Members", true],
                        ["average_monthly_tithes", "Average Monthly Tithes", true],
                        ["average_monthly_remittance", "Average Monthly Remittance", true],
                        ["amount_in_coffers_ldf", "Amount in Coffers - LDF", false],
                        ["emergency_fund_available", "Emergency Fund Available", false],
                        ["building_fund_available", "Building Fund Available", false],
                    ].map(([name, label, auto]) => (
                        <Col xs={24} md={8} key={name}>
                            <Form.Item
                                label={
                                    <Space>
                                        {label}
                                        {auto && <Tag color="blue">Auto</Tag>}
                                    </Space>
                                }
                                name={["cash_flow_analysis", name]}
                                initialValue={cashflowdata[name]}
                            >
                                <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                            </Form.Item>
                        </Col>
                    ))}
                </Row>
            </div>
        </>
    );

    const RiskCost = () => (
        <>
            <SectionHeader
                icon={<SafetyCertificateOutlined/>}
                title="Future Cost, Risk & Income Potential"
                desc="Estimate future expenses, risks, and ministry growth impact."
            />

            <div className="aa-section">
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                        <MetricCard
                            title="Risk Score"
                            value={`${riskScore}%`}
                            icon={<SafetyCertificateOutlined/>}
                        />
                    </Col>
                    <Col xs={24} md={8}>
                        <MetricCard
                            title="Readiness Score"
                            value={`${readinessScore}%`}
                            icon={<CheckCircleOutlined/>}
                            status="success"
                        />
                    </Col>
                    <Col xs={24} md={8}>
                        <MetricCard
                            title="Approval Level"
                            value={approvalLevel}
                            icon={<FileDoneOutlined/>}
                        />
                    </Col>
                </Row>

                <Divider/>

                <Title level={5}>Future Maintenance & Capital Expenditure</Title>

                <Row gutter={[16, 16]}>
                    {[
                        ["roof_replacement", "Roof Replacement"],
                        ["hvac_systems", "HVAC Systems"],
                        ["parking_lots", "Parking Lots"],
                        ["structural_repairs", "Structural Repairs"],
                        ["landscaping", "Landscaping"],
                        ["technology_infrastructure", "Technology Infrastructure"],
                        ["property_insurance", "Property Insurance"],
                        ["liability_insurance", "Liability Insurance"],
                        ["flood_insurance", "Flood Insurance"],
                        ["ministry_coverage", "Ministry Coverage"],
                        ["other_cost", "Other Cost"],
                    ].map(([name, label]) => (
                        <Col xs={24} md={8} key={name}>
                            <Form.Item label={label} name={["future_costs", name]}>
                                <InputNumber  formatter={(value) => formatNumber(value)}  parser={parseNumber} min={0} style={{width: "100%"}}/>
                            </Form.Item>
                        </Col>
                    ))}
                </Row>

                <Divider/>

                <Title level={5}>Income and Revenue Potential</Title>

                {(incomePotential === "Yes" || incomePotential === "No") && (
                    <>
                        <Row gutter={[16, 16]}>
                            {[
                                ["support_ministry_growth", "Will the property support ministry growth?"],
                                ["future_membership", "Can it accommodate future membership increases?"],
                                ["rental_for_events", "Can portions be rented for events or community programs?"],
                                ["reduce_rental_expenses", "Will it reduce current rental expenses?"],
                                ["children_ministry", "Will it accommodate the Children Ministry?"],
                            ].map(([name, label]) => (
                                <Col xs={24} md={12} key={name}>
                                    <Card size="small">
                                        <Form.Item
                                            label={label}
                                            name={["income_potential", name]}
                                            rules={[
                                                { required: true, message: "Please select Yes or No" },
                                            ]}
                                        >
                                            <Radio.Group>
                                                <Radio value="Yes">Yes</Radio>
                                                <Radio value="No">No</Radio>
                                            </Radio.Group>
                                        </Form.Item>

                                        <Form.Item
                                            noStyle
                                            shouldUpdate={(prev, cur) =>
                                                prev?.income_potential?.[name] !==
                                                cur?.income_potential?.[name]
                                            }
                                        >
                                            {({ getFieldValue }) =>
                                                getFieldValue(["income_potential", name]) === "Yes" ? (
                                                    <Form.Item
                                                        label="Explanation"
                                                        name={["income_potential", `${name}_explanation`]}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Explanation is required",
                                                            },
                                                        ]}
                                                    >
                                                        <TextArea rows={3} />
                                                    </Form.Item>
                                                ) : null
                                            }
                                        </Form.Item>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="How many classrooms?"
                                    name={["income_potential", "classrooms"]}
                                    rules={[
                                        {
                                            required: true,
                                            message: "Number of classrooms is required",
                                        },
                                    ]}
                                >
                                    <InputNumber
                                        formatter={(value) => formatNumber(value)}
                                        parser={parseNumber}
                                        min={0}
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}


                <Divider/>

                <Title level={5}>Financial Ratios & Health Indicators</Title>
                <Text type="secondary">
                    These indicators are automatically calculated from the financial information provided above.
                </Text>

                <Row gutter={[16,16]} style={{marginTop:16}}>
                    <Col xs={24} md={8}>
                        <MetricCard
                            title="Liquidity Ratio"
                            value={liquidityRatio}
                            icon={<WalletOutlined />}
                            status={liquidityRatio >= 2 ? "success" : "danger"}
                        />
                    </Col>

                    <Col xs={24} md={8}>
                        <MetricCard
                            title="Debt-to-Income Ratio"
                            value={`${debtIncomeRatio}%`}
                            icon={<DollarOutlined />}
                            status={debtIncomeRatio <= 35 ? "success" : "danger"}
                        />
                    </Col>

                    <Col xs={24} md={8}>
                        <MetricCard
                            title="Operating Reserve"
                            value={`${operatingReserveMonths} Months`}
                            icon={<SafetyCertificateOutlined />}
                            status={operatingReserveMonths >= 3 ? "success" : "danger"}
                        />
                    </Col>

                    <Col xs={24} md={8}>
                        <MetricCard
                            title="Net Asset Position"
                            rawValue={netAssetPosition}
                            icon={<BankOutlined />}
                            status={netAssetPosition >= 0 ? "success" : "danger"}
                        />
                    </Col>

                    <Col xs={24} md={8}>
                        <MetricCard
                            title="Historical Giving Trend"
                            value={givingTrend}
                            icon={<LineChartOutlined />}
                            status={
                                givingTrend === "Growing"
                                    ? "success"
                                    : givingTrend === "Stable"
                                        ? ""
                                        : "danger"
                            }
                        />
                    </Col>
                </Row>

                <Divider />

                <Divider />

                <Title level={5}>Property Acquisition Financial Readiness Checklist</Title>

                <Row gutter={[16, 16]}>
                    {[
                        [
                            "sufficient_funds",
                            "Sufficient funds for down payment or purchase",
                        ],
                        [
                            "emergency_reserves",
                            "Emergency reserves maintained",
                        ],
                        [
                            "budget_approval",
                            "Budget approval obtained",
                        ],
                        [
                            "positive_cash_flow",
                            "Positive cash flow projections",
                        ],
                        [
                            "loan_affordability",
                            "Loan affordability confirmed",
                        ],
                        [
                            "property_valuation",
                            "Independent property valuation completed",
                        ],
                        [
                            "legal_due_diligence",
                            "Legal and due diligence costs budgeted",
                        ],
                        [
                            "insurance_reviewed",
                            "Insurance costs reviewed",
                        ],
                        [
                            "future_maintenance",
                            "Future maintenance costs estimated",
                        ],
                        [
                            "strategic_fit",
                            "Strategic fit confirmed",
                        ],
                        [
                            "board_approval",
                            "Board or leadership approval secured",
                        ],
                        [
                            "risk_assessment_completed",
                            "Risk assessment completed",
                        ],
                    ].map(([name, label]) => (
                        <Col xs={24} key={name}>
                            <Card size="small">
                                <Form.Item
                                    label={label}
                                    name={["financial_checklist", name]}
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please select Yes or No",
                                        },
                                    ]}
                                >
                                    <Radio.Group>
                                        <Radio value="Yes">Yes</Radio>
                                        <Radio value="No">No</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, cur) =>
                                        prev?.financial_checklist?.[name] !==
                                        cur?.financial_checklist?.[name]
                                    }
                                >
                                    {({ getFieldValue }) =>
                                        getFieldValue(["financial_checklist", name]) === "No" ? (
                                            <Form.Item
                                                label="Explanation"
                                                name={[
                                                    "financial_checklist",
                                                    `${name}_explanation`,
                                                ]}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message:
                                                            "Explanation is required when selecting No",
                                                    },
                                                ]}
                                            >
                                                <Input.TextArea
                                                    rows={3}
                                                    placeholder="Please explain..."
                                                />
                                            </Form.Item>
                                        ) : null
                                    }
                                </Form.Item>
                            </Card>
                        </Col>
                    ))}
                </Row>

                <Divider />

                <Title level={5}>Risk Assessment</Title>
                <Text type="secondary">
                    Consider financial, membership, giving, repair, and interest rate risks.
                </Text>

                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    {[
                        ["economic_downturns", "Economic downturns"],
                        ["membership_fluctuations", "Membership fluctuations"],
                        ["giving_pattern_changes", "Changes in giving patterns"],
                        ["unexpected_repair_costs", "Unexpected repair costs"],
                        ["interest_rate_increases", "Interest rate increases"],
                    ].map(([name, label]) => (
                        <Col xs={24} md={12} key={name}>
                            <Card size="small">
                                <Form.Item
                                    label={label}
                                    name={["risk_assessment", name]}
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please select Yes or No",
                                        },
                                    ]}
                                >
                                    <Radio.Group>
                                        <Radio value="Yes">Yes</Radio>
                                        <Radio value="No">No</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prev, cur) =>
                                        prev?.risk_assessment?.[name] !==
                                        cur?.risk_assessment?.[name]
                                    }
                                >
                                    {({ getFieldValue }) =>
                                        getFieldValue(["risk_assessment", name]) === "Yes" ? (
                                            <Form.Item
                                                label="Explanation"
                                                name={["risk_assessment", `${name}_explanation`]}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: "Explanation is required",
                                                    },
                                                ]}
                                            >
                                                <TextArea
                                                    rows={3}
                                                    placeholder="Explain the risk and mitigation plan"
                                                />
                                            </Form.Item>
                                        ) : null
                                    }
                                </Form.Item>
                            </Card>
                        </Col>
                    ))}
                </Row>

            </div>
        </>
    );

    const Documents = () => {
        const docs = [
            ["Letter of Request", "Upload Letter"],
            ["Image of Property or Asset", "Upload Image"],
            ["Other Documents", "Upload Other"],
        ];

        return (
            <>
                <SectionHeader
                    icon={<CloudUploadOutlined/>}
                    title="Documents & Checklist"
                    desc="Upload supporting documents and complete readiness checklist."
                />

                <div className="aa-section">
                    <Row gutter={[16, 16]}>
                        {docs.map(([label, btn]) => (
                            <Col xs={24} md={8} key={label}>
                                <Card bordered={false} className="aa-document-card">
                                    <Space direction="vertical" size={12}>
                                        <div className="aa-document-icon">
                                            <CloudUploadOutlined/>
                                        </div>
                                        <Title level={5}>{label}</Title>
                                        <Text type="secondary">Attach required supporting file.</Text>

                                        <Form.Item label={null}>
                                            <Upload
                                                beforeUpload={() => false}
                                                multiple
                                                listType={label.includes("Image") ? "picture" : "text"}
                                            >
                                                <Button icon={<CloudUploadOutlined/>}>{btn}</Button>
                                            </Upload>
                                        </Form.Item>
                                    </Space>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>
            </>
        );
    };

    const Review = () => (
        <>
            <SectionHeader
                icon={<FileSearchOutlined/>}
                title="Executive Review"
                desc="Final approval-ready summary before submission."
            />

            <Divider />

            <Title level={5}>Finance Department Review</Title>

            <Alert
                type="info"
                showIcon
                message="The Finance Department verifies whether sufficient funds are available within the approved budget and evaluates the organization's financial capacity to acquire the asset."
                style={{ marginBottom: 20 }}
            />

            <Row gutter={[16,16]}>
                <Col xs={24} md={8}>
                    <MetricCard
                        title="Budget Allocation"
                        value={budgetAllocationStatus}
                        icon={<WalletOutlined />}
                        status={budgetAllocationStatus === "Available" ? "success" : "danger"}
                    />
                </Col>

                <Col xs={24} md={8}>
                    <MetricCard
                        title="Funding Source"
                        value={fundingSourceStatus}
                        icon={<BankOutlined />}
                        status={fundingSourceStatus === "Confirmed" ? "success" : "danger"}
                    />
                </Col>

                <Col xs={24} md={8}>
                    <MetricCard
                        title="Affordability"
                        value={affordabilityStatus}
                        icon={<DollarOutlined />}
                        status={affordabilityStatus === "Affordable" ? "success" : "danger"}
                    />
                </Col>
            </Row>

            <Card style={{ marginTop: 20 }}>
                <Descriptions
                    bordered
                    size="small"
                    column={1}
                >
                    <Descriptions.Item label="Budget Allocation">
                        {budgetAllocationRemarks}
                    </Descriptions.Item>

                    <Descriptions.Item label="Funding Source">
                        {fundingSourceRemarks}
                    </Descriptions.Item>

                    <Descriptions.Item label="Affordability Assessment">
                        {affordabilityRemarks}
                    </Descriptions.Item>

                    <Descriptions.Item label="Finance Recommendation">
                        <Tag color={financeRecommendation === "APPROVED" ? "green" : "red"}>
                            {financeRecommendation}
                        </Tag>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            <div className="aa-section">
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={8}>
                        <Card bordered={false} className="aa-review-score-card">
                            <Progress type="circle" percent={readinessScore}/>
                            <Title level={4}>Readiness Score</Title>
                            <Text type="secondary">
                                Combined funding, checklist, and risk readiness.
                            </Text>
                        </Card>
                    </Col>

                    <Col xs={24} lg={16}>
                        <Card bordered={false} className="aa-executive-card">
                            <div className="aa-executive-title">
                                <Title level={4}>Asset Acquisition Summary</Title>
                                <Tag color="blue">{approvalLevel} Approval</Tag>
                            </div>

                            <Divider/>

                            <Row gutter={[16, 16]}>
                                {[
                                    ["Asset Type", reviewValues.asset_type],
                                    ["Priority", reviewValues.priority_level],
                                    ["Method", reviewValues.acquisition_method],
                                    ["Estimated Cost", <AmountDisplay value={estimatedCost}/>],
                                    ["Available Funds", <AmountDisplay value={availableFunds}/>],
                                    ["Funding Gap", <AmountDisplay value={fundingGap}/>],
                                    ["Funding Readiness", `${fundingPercent}%`],
                                    ["Risk Score", `${riskScore}%`],
                                ].map(([label, value]) => (
                                    <Col xs={24} md={12} key={label}>
                                        <div className="aa-review-line">
                                            <Text type="secondary">{label}</Text>
                                            <Text strong>{value || "-"}</Text>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            <Divider/>

                            <Text type="secondary">Justification</Text>
                            <p className="aa-review-text">{reviewValues.justification || "-"}</p>
                        </Card>
                    </Col>

                    <Col xs={24}>
                        <Card bordered={false} className="aa-executive-card">
                            <Title level={5}>Approval Workflow</Title>
                            <Timeline
                                items={[
                                    {color: "green", children: "Local submission"},
                                    {color: "blue", children: "District review"},
                                    {color: "blue", children: "Area review"},
                                    {color: "gray", children: "National approval"},
                                ]}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );

    const contents = [
        <BasicRequest key="basic"/>,
        <AssetDetailsWrapper key="details"/>,
        <Funding key="funding"/>,
        <CashFlow key="cashflow"/>,
        <RiskCost key="risk"/>,
        <Documents key="docs"/>,
        <Review key="review"/>,
    ];

    const renderContent = () => {
        if (current === 0) return BasicRequest();
        if (current === 1) return AssetDetailsWrapper();
        if (current === 2) return Funding();
        if (current === 3) return CashFlow();
        if (current === 4) return RiskCost();
        if (current === 5) return Documents();
        if (current === 6) return Review();

        return BasicRequest();
    };

    const reviewTimerRef = useRef(null);

    const LIVE_FIELDS = [
        "estimated_cost",
        "quantity",
        "financial_readiness",
        "cash_flow_analysis",
        "future_costs",
        "risk_assessment",
    ];

    const handleValuesChange = (changedValues, allValues) => {
        const shouldUpdate = LIVE_FIELDS.some(field =>
            field in changedValues
        );

        if (!shouldUpdate) return;

        clearTimeout(reviewTimerRef.current);

        reviewTimerRef.current = setTimeout(() => {
            setReviewValues(allValues);
        }, 80);
    };

    return (
        <div className="aa-page">
            <div className="aa-shell">
                <div className="aa-hero-dashboard">
                    <Row justify="space-between" align="middle" gutter={[20, 20]}>
                        <Col xs={24} lg={15}>
                            <Space align="start" size={16}>
                                <div className="aa-hero-icon">
                                    <FileTextOutlined />
                                </div>

                                <Space direction="vertical" size={6}>
                                    <div className="aa-eyebrow">Fixed Asset Management</div>

                                    <Title className="aa-hero-title" level={2}>
                                        Asset Acquisition Management
                                    </Title>

                                    <Text className="aa-hero-desc">
                                        Plan, create, and track asset acquisition life cycle.
                                    </Text>

                                    <Space size={8} wrap className="aa-hero-meta">
                                        <Tag color="gold">Draft</Tag>
                                        <Tag>Step {current + 1} of {steps.length}</Tag>
                                    </Space>
                                </Space>
                            </Space>
                        </Col>

                        <Col xs={24} lg={9}>
                            <Row gutter={[12, 12]}>
                                <Col span={12}>
                                    <div className="aa-hero-mini-stat">
                                        <Text>Completion</Text>
                                        <Title level={4}>{completion}%</Title>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div className="aa-hero-mini-stat">
                                        <Text>Approval</Text>
                                        <Title level={4}>{approvalLevel}</Title>
                                    </div>
                                </Col>
                            </Row>

                            <Progress
                                percent={completion}
                                showInfo={false}
                                strokeColor="#1677ff"
                                trailColor="#e5e7eb"
                            />
                        </Col>
                    </Row>
                </div>

                <Row gutter={[16, 16]} className="aa-kpi-row">
                    <Col xs={24} md={6}>
                        <MetricCard
                            title="Estimated Cost"
                            rawValue={estimatedCost}
                            icon={<DollarOutlined/>}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <MetricCard
                            title="Available Funds"
                            rawValue={availableFunds}
                            icon={<WalletOutlined/>}
                            status="success"
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <MetricCard
                            title="Funding Gap"
                            rawValue={fundingGap}
                            icon={<SafetyCertificateOutlined/>}
                            status={fundingGap > 0 ? "danger" : "success"}
                        />
                    </Col>
                    <Col xs={24} md={6}>
                        <MetricCard
                            title="Readiness Score"
                            value={`${readinessScore}%`}
                            icon={<CheckCircleOutlined/>}
                        />
                    </Col>
                </Row>

                <Row gutter={18}>
                    <Col xs={24} lg={6}>
                        <Card className="aa-side-card" bordered={false}>
                            <div className="aa-side-heading">
                                <Text strong>Request Progress</Text>
                                <Text type="secondary">Complete each section</Text>
                            </div>
                            <Steps
                                direction="vertical"
                                current={current}
                                items={steps}
                                // onChange={(step) => setCurrent(step)}
                                onChange={(step) => {
                                    setReviewValues(form.getFieldsValue(true));
                                    setCurrent(step);
                                }}
                            />

                            <Divider/>

                            <Space direction="vertical" size={10} className="aa-side-info">
                                <Text type="secondary">Current Asset</Text>
                                <Tag color="blue">{assetType}</Tag>

                                <Text type="secondary">Estimated Cost</Text>
                                <Tag color="cyan"><AmountDisplay value={estimatedCost}/></Tag>

                                <Text type="secondary">Funding Gap</Text>
                                <Tag color={fundingGap > 0 ? "red" : "green"}>
                                    <AmountDisplay value={fundingGap}/>
                                </Tag>

                                <Text type="secondary">Readiness</Text>
                                <Progress percent={readinessScore} size="small"/>

                                <Text type="secondary">Approval Flow</Text>
                                <Tag color="green">Local → District → Area → National</Tag>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} lg={18}>
                        <Card className="aa-main-card" bordered={false}>
                            <div className="aa-main-card-bar">
                                <div>
                                    <Text className="aa-main-card-kicker">CURRENT SECTION</Text>
                                    <Title className="aa-section-title" level={4}>{steps[current]?.title}</Title>
                                </div>
                                <Tag className="aa-step-tag">{current + 1}/{steps.length}</Tag>
                            </div>
                            <Form
                                form={form}
                                {...CisUI().formItemLayout}
                                className="ant-advanced-search-form aa-form"
                                initialValues={initialFormValues}
                                onValuesChange={handleValuesChange}
                            >
                                <div key={current} className="aa-step-content">
                                    {renderContent()}
                                </div>
                            </Form>

                            <div className="aa-footer">
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        {current > 0 && (
                                            <Button icon={<ArrowLeftOutlined/>} onClick={prev}>
                                                Previous
                                            </Button>
                                        )}
                                    </Col>

                                    <Col>
                                        <Space>
                                            <Button loading={loading} onClick={() => submit("Draft")}>
                                                Save Draft
                                            </Button>

                                            {current < steps.length - 1 ? (
                                                <Button
                                                    type="primary"
                                                    icon={<ArrowRightOutlined/>}
                                                    onClick={next}
                                                >
                                                    Continue
                                                </Button>
                                            ) : (
                                                <Button
                                                    type="primary"
                                                    icon={<SendOutlined/>}
                                                    loading={loading}
                                                    onClick={() => submit("Submitted")}
                                                >
                                                    Submit Request
                                                </Button>
                                            )}
                                        </Space>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </div>
    );
}